import * as React from 'react'
import {useRef, useState} from 'react'
import {observer} from 'mobx-react'
import {Transition} from 'react-transition-group'
import cn from 'classnames'
import {PaymentType} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {buildSlideTransitionProps} from '../../../../lib/helpers'
import {useFocusOut} from '../../../../lib/hooks/useFocusOut'
import {InputCheckboxWrapper} from '../../../wrappers/InputCheckableWrapper'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {PaymentRecurringIntervals} from './PaymentRecurringIntervals'
import {PaymentRecurringIntervalsSimple} from './PaymentRecurringIntervalsSimple'
import {WidgetEventName} from '../../../../lib/WidgetEventer'

///////////////////////////////////////////////////////////////////////////////

export const PaymentPaymentTypes = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const form = api.paymentForm
  const currentPaymentType = form.getPaymentType()

  const isCurrentPaymentTypeOneTime =
    currentPaymentType === PaymentType.ONE_TIME
  const isCurrentPaymentTypeRecurring =
    currentPaymentType === PaymentType.RECURRING

  const isAvailablePaymentTypeOneTime = form
    .getPossiblePaymentTypes()
    .includes(PaymentType.ONE_TIME)
  const isAvailablePaymentTypeRecurring = form
    .getPossiblePaymentTypes()
    .includes(PaymentType.RECURRING)

  const recurringIntervals = form.getPossibleRecurringIntervals()
  const [listShown, setListShown] = useState(false)
  const ref = useRef(undefined)

  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})
  const HeaderTag = isAvailablePaymentTypeOneTime ? 'label' : 'div'

  const headerTagClassNames = cn([
    {'label-checkbox-block': isAvailablePaymentTypeOneTime},
    'recurring-payment-type',
  ])

  const onChange = (e) => {
    const isChecked = e.target.checked

    setTimeout(async () => {
      const value = isChecked ? PaymentType.RECURRING : PaymentType.ONE_TIME
      form.setPaymentType(value)
      await api.events[WidgetEventName.PAYMENT_TYPE_CHANGED].publish({api})

      if (isChecked) {
        recurringIntervals.length > 1 && setListShown(true)
        api.ui.focusWidgetElementBySelector(
          '.payment-payment-types .header input[type=checkbox]',
        )
      } else {
        recurringIntervals.length > 1 && setListShown(false)
        form.setRecurringInterval(form.getFirstPossibleRecurringInterval())
        await api.events[WidgetEventName.RECURRING_INTERVAL_CHANGED].publish({
          api,
        })
      }
    })
  }

  const onClickButtonChangeInterval = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setListShown(true)
    api.ui.focusWidgetElementBySelector(
      '.payment-payment-types .main input[type=radio]:checked',
    )
  }

  const checkbox = (
    <InputCheckboxWrapper>
      <input
        type="checkbox"
        aria-labelledby={api.ui.generateUniqueElementId('payment_type-label')}
        className="input-checkbox"
        checked={form.getPaymentType() === PaymentType.RECURRING}
        onChange={onChange}
      />
    </InputCheckboxWrapper>
  )

  const labelSimple = (
    <span
      className="text"
      role="label"
      id={api.ui.generateUniqueElementId('payment_type-label')}
    >
      {api.trans(
        'blocks.payment_amounts_and_intervals.payment_types.setup_recurring_payment',
      )}
    </span>
  )

  const labelDetailed = (
    <span
      className="text"
      role="label"
      id={api.ui.generateUniqueElementId('payment_type-label')}
    >
      {api.trans(
        `blocks.payment_amounts_and_intervals.payment_types.selected_interval_label.${form.getRecurringInterval()}`,
      )}
    </span>
  )

  const buttonChangeInterval = (
    <button type="button" onClick={onClickButtonChangeInterval}>
      <span className="link">
        {api.trans(
          'blocks.payment_amounts_and_intervals.payment_types.change_interval',
        )}
      </span>
    </button>
  )

  useFocusOut(ref, () => {
    setListShown(false)
  })

  if (!isAvailablePaymentTypeRecurring) {
    return null
  }

  let showSimplePaymentTypeSelection =
    isAvailablePaymentTypeOneTime && recurringIntervals.length === 1

  return (
    <>
      {showSimplePaymentTypeSelection && <PaymentRecurringIntervalsSimple />}
      {!showSimplePaymentTypeSelection && (
        <div className="payment-payment-types" ref={ref}>
          <div className="header">
            <DynamicSlot
              name={DynamicSlotName.PAYMENT_TYPES_START}
              form={form}
            />

            <RippleWrapper disabled={!isAvailablePaymentTypeOneTime}>
              <HeaderTag className={headerTagClassNames}>
                {isAvailablePaymentTypeOneTime && checkbox}

                {isCurrentPaymentTypeOneTime && (
                  <>
                    {recurringIntervals.length === 1 && labelDetailed}
                    {recurringIntervals.length > 1 && labelSimple}
                  </>
                )}

                {isCurrentPaymentTypeRecurring && (
                  <>
                    {listShown && labelSimple}
                    {!listShown && (
                      <>
                        {labelDetailed}
                        {recurringIntervals.length > 1 && buttonChangeInterval}
                      </>
                    )}
                  </>
                )}
              </HeaderTag>
            </RippleWrapper>
          </div>

          <div className="main">
            <Transition
              in={listShown}
              mountOnEnter
              unmountOnExit
              {...slideTransitionProps}
            >
              <PaymentRecurringIntervals setListShown={setListShown} />
            </Transition>

            <DynamicSlot name={DynamicSlotName.PAYMENT_TYPES_END} form={form} />
          </div>
        </div>
      )}
    </>
  )
})
